<template>
    <div v-if="isNew" class="instance-body">
        <!-- A refused Save has nowhere else to appear. The screen's banner
             shows reads only, and an expanded panel covers it outright. -->
        <Message v-if="writeFailure" severity="error" :closable="false" class="instance-body__refusal">
            {{ failureText(writeFailure) }}
        </Message>

        <!-- An Operation is named by identifier on the way in and read back as
             `operationInfo`, so it is in no schema the field controls draw
             from. It has to be asked for by hand, and only while creating. -->
        <section v-if="needsOperation" class="instance-body__section">
            <div class="instance-body__label">
                <span>Operation</span>
                <small>
                    Required. With no Operation there is nothing to resolve a legal identity or a
                    payer through, and the server refuses to create one that way.
                </small>
            </div>
            <DocumentPicker
                :client="needsOperation"
                :model-value="draft?.operationId ?? null"
                placeholder="Search operations…"
                @update:model-value="(value) => setField('operationId', value)"
            />
        </section>

        <section v-for="field in editableFields" :key="field" class="instance-body__section">
            <div class="instance-body__label">
                <span>{{ fieldMeta[field]?.label ?? field }}</span>
            </div>
            <FieldControl
                :field-key="field"
                :field-definitions="fieldDefinitions"
                :field-meta="fieldMeta"
                :value="draft?.[field] ?? null"
                @update:value="(value) => setField(field, value)"
            />
        </section>

        <template v-for="group in editableObjects" :key="group.key">
            <ObjectFieldGroup
                v-if="group.addressAt !== ''"
                :object-key="group.key"
                :label="group.label"
                :address-at="group.addressAt"
                :draft="draft"
                :field-definitions="fieldDefinitions"
                :field-meta="fieldMeta"
                editing
                :set-field="setField"
            />
            <AddressEditor
                v-if="drawsAddress(group, draft)"
                :model-value="addressIn(group, draft)"
                :label="addressLabel(group)"
                :geocode="group.geocode"
                editing
                @update:model-value="(value) => writeAddress(group, draft, value)"
            />
        </template>

        <!-- Relationships are their own routes, so there is nothing to point at
             until the document exists. They appear once it does. -->
        <p v-if="setAfterSaving" class="instance-body__note">
            {{ setAfterSaving }} are set once this is saved — each is its own edit.
        </p>
    </div>

    <div v-else-if="record" class="instance-body">
        <!-- A refused Save has nowhere else to appear. The screen's banner
             shows reads only, and an expanded panel covers it outright. -->
        <Message v-if="writeFailure" severity="error" :closable="false" class="instance-body__refusal">
            {{ failureText(writeFailure) }}
        </Message>

        <section v-for="field in editableFields" :key="field" class="instance-body__section">
            <div class="instance-body__label">
                <span>{{ fieldMeta[field]?.label ?? field }}</span>
            </div>
            <FieldControl
                v-if="editing"
                :field-key="field"
                :field-definitions="fieldDefinitions"
                :field-meta="fieldMeta"
                :value="draft?.[field] ?? null"
                @update:value="(value) => setField(field, value)"
            />
            <FormatValue
                v-else
                class="instance-body__value"
                :record="record"
                :field-key="field"
                :field-definitions="fieldDefinitions"
                :field-meta="fieldMeta"
            />
        </section>

        <template v-for="group in editableObjects" :key="group.key">
            <ObjectFieldGroup
                v-if="group.addressAt !== ''"
                :object-key="group.key"
                :label="group.label"
                :address-at="group.addressAt"
                :draft="editing ? draft : record"
                :field-definitions="fieldDefinitions"
                :field-meta="fieldMeta"
                :editing="editing"
                :set-field="setField"
            />
            <AddressEditor
                v-if="drawsAddress(group, editing ? draft : record)"
                :model-value="addressIn(group, editing ? draft : record)"
                :label="addressLabel(group)"
                :geocode="group.geocode"
                :editing="editing"
                @update:model-value="(value) => writeAddress(group, draft, value)"
            />
        </template>

        <section class="instance-body__lifecycle">
            <div class="instance-body__label">
                <span>Status</span>
                <small>Nothing here is deleted. A dissolved entity is retired and persists, and only Restore brings it back.</small>
            </div>
            <div class="instance-body__control">
                <Button
                    v-if="editing && status === 'retired'"
                    label="Restore"
                    icon="pi pi-replay"
                    severity="secondary"
                    variant="outlined"
                    size="small"
                    :disabled="busy"
                    @click="restore"
                />
                <SelectButton
                    v-else-if="editing"
                    :model-value="status"
                    :options="STATUSES"
                    option-label="label"
                    option-value="value"
                    :allow-empty="false"
                    :disabled="busy"
                    aria-label="Status"
                    @update:model-value="moveTo"
                />
                <Tag v-else :value="statusLabel" severity="secondary" />
                <Tag v-if="operationSuspended" value="Operation suspended" severity="warn" />
                <Tag v-if="busy" value="saving…" severity="secondary" />
            </div>
            <p v-if="failure" class="instance-body__failure">
                {{ failureText(failure) }}
            </p>
        </section>

        <RelationshipPanel
            :record="record"
            :collection="collection"
            :store="store"
            :editing="editing"
        />

        <!-- Companies alone can answer what names them, and it is what somebody
             checks before retiring one. -->
        <section v-if="collection === 'companies'" class="instance-body__referencing">
            <div class="instance-body__label">
                <span>Referenced by</span>
                <small>What names this Company. One that anything still names cannot be retired.</small>
            </div>
            <Button
                label="Show what names it"
                icon="pi pi-sitemap"
                severity="secondary"
                variant="outlined"
                size="small"
                :loading="askingReferencing"
                @click="askReferencing"
            />
            <ul v-if="referencing.length > 0" class="instance-body__list">
                <li v-for="each in referencing" :key="each._id">
                    {{ each.name }}
                    <small>{{ each.collectionName }} · {{ each.roles.join(', ') }}</small>
                </li>
            </ul>
        </section>
    </div>
</template>

<script setup>
import Button from 'primevue/button'
import Message from 'primevue/message'
import SelectButton from 'primevue/selectbutton'
import Tag from 'primevue/tag'
import { computed, ref } from 'vue'

import { ENUM_META } from '@/lib/enumMeta.js'
import { failureText } from '@/lib/failureText.js'
import { RELATIONSHIPS } from '@/lib/relationships.js'
import { labelFor } from '@/lib/fieldLabels.js'
import FieldControl from '@/admin-collection-layout/lib/FieldControl.vue'
import { FormatValue } from '@/admin-collection-layout/lib/formatValue.js'
import AddressEditor from '@/components/AddressEditor.vue'
import ObjectFieldGroup from '@/components/ObjectFieldGroup.vue'
import DocumentPicker from '@/components/relationships/DocumentPicker.vue'
import RelationshipPanel from '@/components/relationships/RelationshipPanel.vue'

// What the instance panel shows for an administration document.
//
// It draws the document's own fields as well as its relationships, and that is
// not a choice: the panel renders `#instance` **instead of** its own field
// form, not beside it. A body supplying only the extras leaves a record with no
// name box — which is what this one did until somebody pressed New and found
// nothing to type into.
const props = defineProps({
    record: { type: Object, default: null },
    draft: { type: Object, default: null },
    collection: { type: String, required: true },
    store: { type: Object, required: true },
    isNew: { type: Boolean, default: false },
    // The SDK client of the Operation this noun belongs to, or null when it
    // belongs to none. Supplied by the collection's config.
    needsOperation: { type: String, default: null },
    // Writes one key into the panel's draft — the draft is what gets saved.
    setField: { type: Function, default: null },
    // What a PATCH accepts, in the order it should be shown.
    editableFields: { type: Array, default: () => [] },
    // Nested properties a PATCH also takes — a Company's billing facts, a
    // house's address. Drawn as a group of the leaves the surface publishes.
    editableObjects: { type: Array, default: () => [] },
    fieldDefinitions: { type: Object, default: () => ({ fields: {} }) },
    fieldMeta: { type: Object, default: () => ({}) },
    editing: { type: Boolean, default: false },
    // What the last Save or Create was refused with, or null. Held by the
    // collection composable, because the panel's own submit answers nothing.
    writeFailure: { type: Object, default: null }
})

/**
 * Where an address sits, and whether there is one to draw.
 *
 * A group declaring `addressAt: ''` **is** the address — a house's location —
 * so the generic group is replaced outright. A group declaring a path holds one
 * among other properties — a Company's billing facts — so the generic group
 * stays for the rest and the editor is drawn beneath it.
 *
 * A nested address needs its parent to exist first: there is nowhere to put an
 * address on a `billing` nobody has added, and the parent's own Add button is
 * what creates it.
 */
function drawsAddress(group, source) {
    if (group.addressAt === undefined) {
        return false
    }
    if (group.addressAt === '') {
        return true
    }
    return source?.[group.key] !== undefined && source?.[group.key] !== null
}

function addressIn(group, source) {
    const held = source?.[group.key] ?? null
    if (group.addressAt === '') {
        return held
    }
    return held?.[group.addressAt] ?? null
}

/**
 * "Location" for a group that is the address, "Billing address" for one that
 * holds it — built from the path so it reads as what it is rather than
 * repeating the parent's heading.
 */
function addressLabel(group) {
    if (group.addressAt === '') {
        return group.label
    }
    return labelFor(group.addressAt)
}

/**
 * The editor answers with an address; the draft takes whole keys. So a nested
 * one is placed into a copy of its parent and the parent is what gets written,
 * the same move `ObjectFieldGroup` makes for its leaves.
 */
function writeAddress(group, draft, value) {
    if (group.addressAt === '') {
        props.setField(group.key, value)
        return
    }
    props.setField(group.key, { ...draft?.[group.key], [group.addressAt]: value })
}

// The same wording and order the list uses, so a status reads one way everywhere.
const STATUSES = Object.entries(ENUM_META.status).map(([value, { label }]) => ({ label, value }))

const AND_LIST = new Intl.ListFormat('en', { type: 'conjunction' })

// What this noun's relationships panel will offer once the document exists,
// named from that panel's own rows. The Operation is left out, because creating
// asks for it above, and so are the managed rosters, which other records fill.
const setAfterSaving = computed(function () {
    const labels = (RELATIONSHIPS[props.collection] ?? [])
        .filter((row) => row.kind !== 'managed' && row.key !== 'operationInfo')
        .map((row, index) => (index === 0 ? row.label : row.label.toLowerCase()))
    return AND_LIST.format(labels)
})

const busy = ref(false)
const failure = ref(null)

const status = computed(() => props.record?.status ?? null)

// What the buttons are replaced by while nobody is editing. A value the
// lifecycle does not name reads as a dash rather than as a status it is not.
const statusLabel = computed(() => ENUM_META.status[status.value]?.label ?? '—')

// A suspended Operation holds every house or dealership it runs without
// changing their own status, so the hold is shown beside the status rather than
// in place of it. The copy rides in `operationInfo`; a noun with no Operation
// has none.
const operationSuspended = computed(() => props.record?.operationInfo?.status === 'suspended')

/**
 * Lifecycle, not deletion.
 *
 * Nothing on this server deletes — a Company that was dissolved is retired and
 * persists, because documents that named it still say so. The list's Delete
 * button is hidden for the same reason; this is the control that replaces it,
 * and it says what actually happens.
 *
 * The refusals are the point of showing them here. Retiring a Company that
 * anything still names answers `COMPANY_STILL_IN_USE`, and retiring an
 * Operation that still runs a house answers `OPERATION_STILL_IN_USE`. Each
 * names the document to deal with first, which is a sentence about the
 * platform rather than about this screen.
 */
async function moveTo(next) {
    if (next === null || next === status.value) {
        return
    }
    await settleMove(() => props.store.setStatus(props.record._id, next))
}

/**
 * The only way out of retired. The buttons are not offered on a retired record,
 * because the server refuses every move they could make (`RESTORE_REQUIRED`).
 * A restore that would bring back a reference to something retired is refused
 * too, and reads here like any other refusal.
 */
async function restore() {
    await settleMove(() => props.store.restore(props.record._id))
}

async function settleMove(move) {
    busy.value = true
    failure.value = null
    const ok = await move()
    if (ok === false) {
        failure.value = props.store.error
    } else {
        // A move answers the updated document into `current` and touches
        // the page nothing else reads. This panel reads `record`, which comes
        // off that page — so without the re-read the buttons snapped back to
        // the old status after a move the server had accepted.
        await props.store.refresh()
    }
    busy.value = false
}

/**
 * Its own flag, not the store's.
 *
 * `store.loading` is set by every read the collection makes, so binding the
 * button to it spun the spinner while somebody typed in the search bar.
 */
const askingReferencing = ref(false)

/**
 * Which Company the held list describes.
 *
 * `store.referencing` outlives the record it was read for, so opening a second
 * Company rendered the first one's referrers under the second one's heading,
 * with nobody having asked. The list is shown only while this still names the
 * record on screen.
 */
const referencingFor = ref(null)

const referencing = computed(function () {
    if (referencingFor.value !== props.record?._id) {
        return []
    }
    return props.store.referencing
})

async function askReferencing() {
    askingReferencing.value = true
    const ok = await props.store.loadReferencing(props.record._id)
    referencingFor.value = ok === false ? null : props.record._id
    askingReferencing.value = false
}
</script>

<style scoped>
.instance-body {
    display: flex;
    flex-direction: column;
}

.instance-body__section,
.instance-body__lifecycle,
.instance-body__referencing {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 0;
}

.instance-body__referencing {
    border-top: 1px solid var(--p-content-border-color);
    margin-top: 16px;
    padding-top: 16px;
}

.instance-body__label {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.instance-body__label span {
    font-size: 13px;
    font-weight: 600;
}

.instance-body__label small {
    color: var(--p-surface-400);
    font-size: 11px;
    line-height: 1.4;
}

.instance-body__control {
    align-items: center;
    display: flex;
    gap: 8px;
}

.instance-body__failure {
    color: var(--p-status-danger);
    font-size: 12px;
    margin: 4px 0 0;
}

.instance-body__refusal {
    margin: 0 0 12px;
}

.instance-body__value {
    font-size: 13px;
}

.instance-body__note {
    border-top: 1px solid var(--p-content-border-color);
    color: var(--p-surface-400);
    font-size: 12px;
    margin: 12px 0 0;
    padding-top: 12px;
}

.instance-body__list {
    list-style: none;
    margin: 8px 0 0;
    padding: 0;
}

.instance-body__list li {
    border-bottom: 1px solid var(--p-content-border-color);
    font-size: 13px;
    padding: 6px 0;
}

.instance-body__list small {
    color: var(--p-surface-400);
    display: block;
    font-size: 11px;
}
</style>
